import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCubes, faArrowLeft, faSave, faInfoCircle, faUserCheck, faComment, faTable, faMagic } from '@fortawesome/free-solid-svg-icons';
import { getReviewers } from '../../../apis/reviewers';
import { getCustomers } from '../../../apis/customers';
import { 
  createCoarseAggregateTestRequest, 
  saveCoarseAggregateObservations,
  getCoarseAggregateTestRequest,
  updateCoarseAggregateTestRequest,
  getCoarseAggregateObservations
} from '../../../apis/coarseAggregate';

const CoarseAggregateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentForm, setCurrentForm] = useState(1);
  const [selectedTests, setSelectedTests] = useState([]);
  const [reviewerInfo, setReviewerInfo] = useState({
    name: 'Lalita S. Dussa',
    designation: 'Quality Manager',
    graduation: 'B.Tech.(Civil)'
  });
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  
  // Customer data state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersError, setCustomersError] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [testRequestId, setTestRequestId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomersData = async () => {
      try {
        setCustomersLoading(true);
        setCustomersError(null);
        const customersData = await getCustomers();
        setCustomers(customersData);
        setFilteredCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomersError('Failed to load customers');
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomersData();
  }, []);

  // Filter customers based on dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer => {
        const customerName = customer.name || customer.client_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
        return customerName.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
               (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
               (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()));
      });
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer) => {
    const customerName = customer.name || customer.client_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customerName
    }));
    setCustomerSearchTerm(customerName);
    setShowCustomerDropdown(false);
    setDropdownSearchTerm(''); // Clear dropdown search
  };

  // Handle dropdown search input
  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  // Handle customer input click to show dropdown
  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm(''); // Clear search when opening dropdown
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Add style CSS to override table-info background color and prevent hover effects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info:hover {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info td {
        background-color: #495057 !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      .table tbody tr.table-info:hover td {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info:hover {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info td {
        background-color: #495057 !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      .transparent-table tbody tr.table-info:hover td {
        background-color: #495057 !important;
      }
      
      .transparent-table thead.table-dark th {
        background-color: #6c757d !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      .table-dark th {
        background-color: #6c757d !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      /* Style for Form.Select dropdowns - grey background for better visibility */
      .form-select {
        background-color: #495057 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
      }
      
      .form-select:focus {
        background-color: #495057 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .form-select option {
        background-color: #495057 !important;
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Load test request data if testRequestId is in URL (for edit/view mode)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testRequestIdFromUrl = urlParams.get('testRequestId');
    const isEditModeFromUrl = urlParams.get('edit') === 'true';
    
    if (testRequestIdFromUrl) {
      console.log('🔵 [COARSE AGGREGATE FORM] Loading test request from URL, ID:', testRequestIdFromUrl);
      console.log('🔵 [COARSE AGGREGATE FORM] Edit mode:', isEditModeFromUrl);
      setTestRequestId(parseInt(testRequestIdFromUrl));
      setIsEditMode(isEditModeFromUrl);
      
      const loadTestData = async () => {
        try {
          setIsLoadingData(true);
          const testData = await getCoarseAggregateTestRequest(parseInt(testRequestIdFromUrl));
          console.log('✅ [COARSE AGGREGATE FORM] Test request data loaded:', testData);
          
          // Update formData with loaded test request data
          setFormData(prev => ({
            ...prev,
            customer_id: testData.customer_id || '',
            customer_name: testData.customer_name || '',
            job_code_number: testData.job_number || '',
            url_number: testData.ulr_number || '',
            ulr_number: testData.ulr_number || '',
            reference_number: testData.reference_number || '',
            date_of_material_receipt: testData.receipt_date || '',
            date_of_receipt: testData.receipt_date || '',
            customer_site_address: testData.site_name || '',
            location_of_testing: 'Lab' // Always set to constant value "Lab"
          }));
          
          // Load observation data if exists
          if (testData.observation) {
            const obs = testData.observation;
            
            // Load selected tests
            if (obs.selected_tests && Array.isArray(obs.selected_tests)) {
              setSelectedTests(obs.selected_tests);
            }
            
            // Load form data from observation
            if (obs.form_data) {
              setFormData(prev => ({
                ...prev,
                ...obs.form_data,
                sample_description: obs.sample_description || obs.form_data.sample_description || '',
                test_method: obs.test_method || obs.form_data.test_method || 'IS 2386',
                date_of_testing: obs.date_of_testing || obs.form_data.date_of_testing || new Date().toISOString().split('T')[0],
                location_of_testing: 'Lab', // Always set to constant value "Lab"
                tested_by_name: obs.tested_by_name || obs.form_data.tested_by_name || '',
                tested_by_date: obs.tested_by_date || obs.form_data.tested_by_date || '',
                checked_by_name: obs.checked_by_name || obs.form_data.checked_by_name || '',
                checked_by_date: obs.checked_by_date || obs.form_data.checked_by_date || '',
                verified_by_name: obs.verified_by_name || obs.form_data.verified_by_name || '',
                verified_by_date: obs.verified_by_date || obs.form_data.verified_by_date || '',
                remarks: obs.remarks || obs.form_data.remarks || ''
              }));
            }
            
            // Load reviewer info
            if (obs.reviewer_name) {
              setReviewerInfo({
                name: obs.reviewer_name || '',
                designation: obs.reviewer_designation || '',
                graduation: obs.reviewer_graduation || ''
              });
            }
            
            // Load test results if exists
            if (obs.test_results) {
              // Test results are already in formData structure, they'll be loaded via form_data
            }
          }
          
          console.log('✅ [COARSE AGGREGATE FORM] Form data loaded successfully');
        } catch (error) {
          console.error('❌ [COARSE AGGREGATE FORM] Error loading test data:', error);
          setSubmitError('Failed to load test data. Please try again.');
        } finally {
          setIsLoadingData(false);
        }
      };
      
      loadTestData();
    }
  }, []);

  // Handle selected tests from popup
  useEffect(() => {
    if (location.state && location.state.selectedTests) {
      setSelectedTests(location.state.selectedTests);
      // Set current form to first available test (always start with 1)
      setCurrentForm(1);
    } else if (!testRequestId) {
      // Default to all tests if no selection made and not in edit mode
      setSelectedTests(['bulkDensity', 'sieveAnalysis', 'specificGravity', 'flakinessElongation', 'impactValue', 'crushingValue']);
      setCurrentForm(1);
    }
  }, [location.state, testRequestId]);

  // Fetch reviewers from database
  useEffect(() => {
    const fetchReviewers = async () => {
      setLoadingReviewers(true);
      try {
        const reviewersData = await getReviewers();
        setReviewers(reviewersData);
        
        // Set default reviewer if available
        if (reviewersData.length > 0) {
          const defaultReviewer = reviewersData[0];
          setReviewerInfo({
            name: defaultReviewer.name,
            designation: defaultReviewer.designation,
            graduation: defaultReviewer.graduation
          });
        }
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        // Fallback to hardcoded reviewers if API fails
        setReviewers([
          { id: 1, name: 'Lalita S. Dussa', designation: 'Quality Manager', graduation: 'B.Tech.(Civil)' },
          { id: 2, name: 'Harsha Prakarsha Sangave', designation: 'Quality Manager', graduation: 'M.E(Civil-Structures)' },
          { id: 3, name: 'Amol A Adam', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 4, name: 'Aaquib J. Shaikh', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 5, name: 'Prakarsha A. Sangave', designation: 'Chief Executive Officer', graduation: 'M.E(Civil-Structures)' }
        ]);
      } finally {
        setLoadingReviewers(false);
      }
    };

    fetchReviewers();
  }, []);

  // Add CSS styles to match reference template
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .auto-calculated {
        background-color: #242B3C !important;
        color: white !important;
        cursor: not-allowed !important;
      }
      
      .auto-calculated:focus {
        box-shadow: none !important;
        border-color: #ced4da !important;
      }
      
      .loose-weight-input {
        background-color: white !important;
        color: #000 !important;
        cursor: text !important;
        pointer-events: auto !important;
      }
      
      .loose-weight-input:focus {
        background-color: white !important;
        border-color: #007bff !important;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
      }
      
      .table td,
      .table td *,
      .table input,
      .table strong,
      .table .form-control {
        text-align: center !important;
        vertical-align: middle !important;
      }
      
      .table td.left-align-text {
        text-align: left !important;
      }
      
      .table td.left-align-text strong {
        text-align: left !important;
      }
      
      .table th,
      .table th * {
        text-align: center !important;
        vertical-align: middle !important;
      }
      
      .coarse-aggregate-header {
        background-color: #007bff !important;
        color: white !important;
        border-radius: 15px !important;
      }
      
      .coarse-aggregate-header .card-body {
        background-color: #007bff !important;
        color: white !important;
        border-radius: 15px !important;
      }
      
      .coarse-aggregate-header h1,
      .coarse-aggregate-header p {
        color: white !important;
      }
      
      .step-navigation .btn-outline-primary {
        border: 2px solid #007bff !important;
        background-color: transparent !important;
        color: #007bff !important;
      }
      
      .step-navigation .btn-outline-primary:hover {
        border: 2px solid #007bff !important;
        background-color: rgba(0, 123, 255, 0.1) !important;
        color: #007bff !important;
      }
      
      .step-navigation .btn-primary {
        background-color: #007bff !important;
        border-color: #007bff !important;
        color: white !important;
      }
      
                .step-navigation .btn-primary:hover {
                  background-color: #0056b3 !important;
                  border-color: #0056b3 !important;
                  color: white !important;
                }
                .table td.left-align-text {
                  text-align: left !important;
                }
                .table td.left-align-text strong {
                  text-align: left !important;
                }
                
                /* Cube Testing Style for Form Controls */
                .form-control, .form-select {
                  background-color: #1C2333 !important;
                  color: white !important;
                  border: 2px solid #6c757d !important;
                }
                
                .form-control:focus, .form-select:focus {
                  background-color: #1C2333 !important;
                  color: white !important;
                  border-color: #FFA500 !important;
                  box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
                }
                
                .form-control::placeholder {
                  color: #adb5bd !important;
                }
                
                .form-select option {
                  background-color: #1C2333 !important;
                  color: white !important;
                }
                
                .card {
                  background-color: #1C2333 !important;
                  border: 2px solid #6c757d !important;
                }
                
                .card-header {
                  background-color: #6c757d !important;
                  color: white !important;
                }
                
                .card-body {
                  background-color: #1C2333 !important;
                  color: white !important;
                }
                
                .list-group-item {
                  background-color: #1C2333 !important;
                  color: white !important;
                  border: 1px solid #6c757d !important;
                }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [formData, setFormData] = useState({
    // General Information
    customer_id: '',
    customer_name: '',
    sample_description: '',
    date_of_receipt: '', // Keep for backward compatibility
    date_of_material_receipt: '', // Used in TEST REPORT table
    proposed_use: '',
    test_method: '',
    sample_test_code: '',
    date_of_testing: new Date().toISOString().split('T')[0],
    source: '',
    environmental_conditions: '',
    
    // Test Report Details (from TEST REPORT table)
    customer_site_address: '',
    date_of_report: '',
    reference_number: '',
    type_grade_aggregate: '',
    url_number: '', // ULR Number
    ulr_number: '', // Alias for url_number
    job_code_number: '',
    period_of_testing: '',
    condition_of_sample: 'Acceptable',
    location_of_testing: 'Lab', // Constant value - always "Lab"
    location: '', // Separate from location_of_testing
    source_of_material: '', // Separate from source
    
    // Form-1: Bulk Density
    volume_measure_cylinder_1: '', net_weight_rodded_1: '', rodded_bulk_density_1: '',
    volume_measure_cylinder_2: '', net_weight_rodded_2: '', rodded_bulk_density_2: '',
    volume_measure_cylinder_3: '', net_weight_rodded_3: '', rodded_bulk_density_3: '',
    net_weight_loose_1: '', loose_bulk_density_1: '',
    net_weight_loose_2: '', loose_bulk_density_2: '',
    net_weight_loose_3: '', loose_bulk_density_3: '',
    
    // Form-1: Sieve Analysis
    total_weight: '',
    mean_rodded_bulk_density: '', mean_loose_bulk_density: '',
    sum_cumulative_weight_retained: '',
    fineness_modulus: '',
    // Sieve Analysis Table Fields
    sieve_weight_retained_40mm: '', sieve_percent_weight_retained_40mm: '', sieve_cumulative_percent_weight_retained_40mm: '', sieve_percent_passing_40mm: '',
    sieve_weight_retained_20mm: '', sieve_percent_weight_retained_20mm: '', sieve_cumulative_percent_weight_retained_20mm: '', sieve_percent_passing_20mm: '',
    sieve_weight_retained_12_5mm: '', sieve_percent_weight_retained_12_5mm: '', sieve_cumulative_percent_weight_retained_12_5mm: '', sieve_percent_passing_12_5mm: '',
    sieve_weight_retained_10mm: '', sieve_percent_weight_retained_10mm: '', sieve_cumulative_percent_weight_retained_10mm: '', sieve_percent_passing_10mm: '',
    sieve_weight_retained_4_75mm: '', sieve_percent_weight_retained_4_75mm: '', sieve_cumulative_percent_weight_retained_4_75mm: '', sieve_percent_passing_4_75mm: '',
    sieve_weight_retained_2_36mm: '', sieve_percent_weight_retained_2_36mm: '', sieve_cumulative_percent_weight_retained_2_36mm: '', sieve_percent_passing_2_36mm: '',
    sieve_weight_retained_1_18mm: '', sieve_percent_weight_retained_1_18mm: '', sieve_cumulative_percent_weight_retained_1_18mm: '', sieve_percent_passing_1_18mm: '',
    sieve_weight_retained_600um: '', sieve_percent_weight_retained_600um: '', sieve_cumulative_percent_weight_retained_600um: '', sieve_percent_passing_600um: '',
    sieve_weight_retained_300um: '', sieve_percent_weight_retained_300um: '', sieve_cumulative_percent_weight_retained_300um: '', sieve_percent_passing_300um: '',
    sieve_weight_retained_150um: '', sieve_percent_weight_retained_150um: '', sieve_cumulative_percent_weight_retained_150um: '', sieve_percent_passing_150um: '',
    sieve_weight_retained_pan: '', sieve_percent_weight_retained_pan: '', sieve_cumulative_percent_weight_retained_pan: '', sieve_percent_passing_pan: '',
    
    // Form-5: Bulk Density
    volume_01: '', volume_02: '', volume_03: '',
    rodded_weight_01: '', rodded_weight_02: '', rodded_weight_03: '',
    rodded_density_01: '', rodded_density_02: '', rodded_density_03: '',
    loose_weight_01: '', loose_weight_02: '', loose_weight_03: '',
    loose_density_01: '', loose_density_02: '', loose_density_03: '',
    mean_rodded_density: '', mean_loose_density: '',
    
    // Form-2: Sieve Analysis
    type_size_aggregate_2: '',
    total_load_10_minutes: '',
    sieve_40mm_1: '', sieve_40mm_2: '', sieve_40mm_3: '', sieve_40mm_mean: '',
    sieve_20mm_1: '', sieve_20mm_2: '', sieve_20mm_3: '', sieve_20mm_mean: '',
    sieve_10mm_1: '', sieve_10mm_2: '', sieve_10mm_3: '', sieve_10mm_mean: '',
    sieve_4_75mm_1: '', sieve_4_75mm_2: '', sieve_4_75mm_3: '', sieve_4_75mm_mean: '',
    sieve_2_36mm_1: '', sieve_2_36mm_2: '', sieve_2_36mm_3: '', sieve_2_36mm_mean: '',
    zone_i_1: '', zone_i_2: '', zone_i_3: '', zone_i_mean: '',
    zone_ii_1: '', zone_ii_2: '', zone_ii_3: '', zone_ii_mean: '',
    zone_iii_1: '', zone_iii_2: '', zone_iii_3: '', zone_iii_mean: '',
    zone_iv_1: '', zone_iv_2: '', zone_iv_3: '', zone_iv_mean: '',
    
    // Form-3: Specific Gravity & Water Absorption
    type_of_aggregate_3: '',
    max_nominal_size_3: '',
    wt_saturated_surface_dry_1: '', wt_pycnometer_aggregate_water_1: '', wt_pycnometer_water_1: '', wt_oven_dried_1: '',
    wt_saturated_surface_dry_2: '', wt_pycnometer_aggregate_water_2: '', wt_pycnometer_water_2: '', wt_oven_dried_2: '',
    wt_saturated_surface_dry_3: '', wt_pycnometer_aggregate_water_3: '', wt_pycnometer_water_3: '', wt_oven_dried_3: '',
    specific_gravity_1: '', water_absorption_1: '',
    specific_gravity_2: '', water_absorption_2: '',
    specific_gravity_3: '', water_absorption_3: '',
    mean_specific_gravity: '', mean_water_absorption: '',
    
    // Form-4: Flakiness Index
    type_of_aggregate_4: '',
    max_nominal_size_4: '',
    flakiness_10mm_1: '', flakiness_10mm_2: '', flakiness_10mm_3: '', flakiness_10mm_mean: '',
    flakiness_20mm_1: '', flakiness_20mm_2: '', flakiness_20mm_3: '', flakiness_20mm_mean: '',
    total_flakiness_index: '',
    // Form-4: Flakiness & Elongation Table Fields
    flakiness_weight_sample_1: '', flakiness_weight_sample_2: '', flakiness_weight_sample_3: '',
    flakiness_weight_flaky_1: '', flakiness_weight_flaky_2: '', flakiness_weight_flaky_3: '',
    flakiness_index_1: '', flakiness_index_2: '', flakiness_index_3: '',
    mean_flakiness_index: '',
    elongation_weight_elongated_1: '', elongation_weight_elongated_2: '', elongation_weight_elongated_3: '',
    elongation_index_1: '', elongation_index_2: '', elongation_index_3: '',
    mean_elongation_index: '',
    
    // Form-5: Elongation Index
    type_size_aggregate_5: '',
    condition_of_aggregate: '',
    elongation_10mm_1: '', elongation_10mm_2: '', elongation_10mm_3: '', elongation_10mm_mean: '',
    elongation_20mm_1: '', elongation_20mm_2: '', elongation_20mm_3: '', elongation_20mm_mean: '',
    total_elongation_index: '',
    
    // Form-6: Impact Value
    type_of_aggregate_6: '',
    max_nominal_size_6: '',
    impact_weight_1: '', impact_weight_2: '', impact_weight_3: '',
    impact_passing_1: '', impact_passing_2: '', impact_passing_3: '',
    impact_retained_1: '', impact_retained_2: '', impact_retained_3: '',
    impact_total_weight_1: '', impact_total_weight_2: '', impact_total_weight_3: '',
    impact_value_1: '', impact_value_2: '', impact_value_3: '',
    mean_impact_value: '',
    
    // Form-2: Crushing Value Table Fields
    crushing_weight_sample_1: '', crushing_weight_sample_2: '',
    crushing_weight_passing_1: '', crushing_weight_passing_2: '',
    crushing_value_1: '', crushing_value_2: '',
    mean_crushing_value: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa',
    reviewed_by_designation: 'Quality Manager',
    reviewed_by_graduation: 'B.Tech.(Civil)',
    authorized_by: 'Prakarsh Sangave',
    remarks: ''
  });

  const allFormTitles = [
    { id: 1, title: 'Bulk Density', description: 'Determination of Bulk Density of Coarse Aggregate', testId: 'bulkDensity' },
    { id: 2, title: 'Sieve Analysis', description: 'Grading of Coarse Aggregate by Sieve Analysis', testId: 'sieveAnalysis' },
    { id: 3, title: 'Specific Gravity & Water Absorption', description: 'Determination of Specific Gravity and Water Absorption', testId: 'specificGravity' },
    { id: 4, title: 'Flakiness & Elongation Index', description: 'Determination of Flakiness & Elongation Index of Aggregate', testId: 'flakinessElongation' },
    { id: 5, title: 'Impact Value', description: 'Determination of Impact Value', testId: 'impactValue' },
    { id: 6, title: 'Crushing Value', description: 'Determination of Aggregate Crushing Value', testId: 'crushingValue' }
  ];

  // Filter form titles based on selected tests
  const formTitles = allFormTitles.filter(form => selectedTests.includes(form.testId));


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent changing location_of_testing - always keep it as "Lab"
    if (name === 'location_of_testing') {
      return; // Don't update - keep constant value "Lab"
    }
    
    const newValue = value || ''; // Ensure value is always a string
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Auto-calculate based on current form
    if (currentForm === 5) {
      calculateBulkDensity(name, newValue);
    }
    
    // Add Sieve Analysis calculations (Form 2)
    // Trigger when any sieve weight retained (B) changes
    if (currentForm === 2 && name.includes('sieve_weight_retained_')) {
      calculateSieveAnalysis();
    }
    
    // Add Crushing Value calculations (Form 6)
    // Trigger when crushing weight sample (A) or crushing weight passing (B) changes
    if (currentForm === 6 && (name.includes('crushing_weight_sample_') || name.includes('crushing_weight_passing_'))) {
      calculateCrushingValue();
    }
    
    // Add Flakiness Index calculations (Form 4)
    // Trigger when flakiness weight sample (A) or flakiness weight flaky (B) changes
    if (currentForm === 4 && (name.includes('flakiness_weight_sample_') || name.includes('flakiness_weight_flaky_'))) {
      calculateFlakinessIndex();
    }
    
    // Add Elongation Index calculations (Form 4)
    // Trigger when elongation weight elongated (C) or flakiness weight sample (A) changes
    if (currentForm === 4 && (name.includes('elongation_weight_elongated_') || name.includes('flakiness_weight_sample_'))) {
      calculateElongationIndex();
    }
    
    // Log sample_description changes for debugging
    if (name === 'sample_description') {
      console.log('📝 Sample Description changed to:', newValue);
    }
  };

  // Debug: Track sample_description value to identify reset issues
  useEffect(() => {
    console.log('🔍 Sample Description Debug - Value:', formData.sample_description, '| Current Form:', currentForm);
  }, [formData.sample_description, currentForm]);

  // Auto-calculate Specific Gravity, Impact Value, and Sieve Analysis when formData changes
  useEffect(() => {
    // Calculate Specific Gravity if specificGravity is selected and we have relevant data
    if (selectedTests.includes('specificGravity') && 
        (formData.wt_pycnometer_aggregate_water_1 || formData.wt_pycnometer_aggregate_water_2 || formData.wt_pycnometer_aggregate_water_3 ||
         formData.wt_pycnometer_water_1 || formData.wt_pycnometer_water_2 || formData.wt_pycnometer_water_3 ||
         formData.wt_saturated_surface_dry_1 || formData.wt_saturated_surface_dry_2 || formData.wt_saturated_surface_dry_3 ||
         formData.wt_oven_dried_1 || formData.wt_oven_dried_2 || formData.wt_oven_dried_3)) {
      calculateSpecificGravity();
    }
    
    // Calculate Impact Value if impactValue is selected and we have relevant data
    if (selectedTests.includes('impactValue') && 
        (formData.impact_weight_1 || formData.impact_weight_2 || formData.impact_weight_3 ||
         formData.impact_passing_1 || formData.impact_passing_2 || formData.impact_passing_3 ||
         formData.impact_retained_1 || formData.impact_retained_2 || formData.impact_retained_3)) {
      calculateImpactValue();
    }
    
    // Calculate Sieve Analysis if sieveAnalysis is selected and we have weight retained data
    if (selectedTests.includes('sieveAnalysis') && 
        (formData.sieve_weight_retained_40mm || formData.sieve_weight_retained_20mm || formData.sieve_weight_retained_12_5mm ||
         formData.sieve_weight_retained_10mm || formData.sieve_weight_retained_4_75mm || formData.sieve_weight_retained_2_36mm ||
         formData.sieve_weight_retained_1_18mm || formData.sieve_weight_retained_600um || formData.sieve_weight_retained_300um ||
         formData.sieve_weight_retained_150um || formData.sieve_weight_retained_pan)) {
      calculateSieveAnalysis();
    }
    
    // Calculate Crushing Value if crushingValue is selected and we have relevant data
    if (selectedTests.includes('crushingValue') && 
        (formData.crushing_weight_sample_1 || formData.crushing_weight_sample_2 ||
         formData.crushing_weight_passing_1 || formData.crushing_weight_passing_2)) {
      calculateCrushingValue();
    }
    
    // Calculate Flakiness Index if flakinessElongation is selected and we have relevant data
    if (selectedTests.includes('flakinessElongation') && 
        (formData.flakiness_weight_sample_1 || formData.flakiness_weight_sample_2 || formData.flakiness_weight_sample_3 ||
         formData.flakiness_weight_flaky_1 || formData.flakiness_weight_flaky_2 || formData.flakiness_weight_flaky_3)) {
      calculateFlakinessIndex();
    }
    
    // Calculate Elongation Index if flakinessElongation is selected and we have relevant data
    if (selectedTests.includes('flakinessElongation') && 
        (formData.elongation_weight_elongated_1 || formData.elongation_weight_elongated_2 || formData.elongation_weight_elongated_3 ||
         formData.flakiness_weight_sample_1 || formData.flakiness_weight_sample_2 || formData.flakiness_weight_sample_3)) {
      calculateElongationIndex();
    }
  }, [formData.wt_pycnometer_aggregate_water_1, formData.wt_pycnometer_aggregate_water_2, formData.wt_pycnometer_aggregate_water_3,
      formData.wt_pycnometer_water_1, formData.wt_pycnometer_water_2, formData.wt_pycnometer_water_3,
      formData.wt_saturated_surface_dry_1, formData.wt_saturated_surface_dry_2, formData.wt_saturated_surface_dry_3,
      formData.wt_oven_dried_1, formData.wt_oven_dried_2, formData.wt_oven_dried_3,
      formData.impact_weight_1, formData.impact_weight_2, formData.impact_weight_3,
      formData.impact_passing_1, formData.impact_passing_2, formData.impact_passing_3,
      formData.impact_retained_1, formData.impact_retained_2, formData.impact_retained_3,
      formData.sieve_weight_retained_40mm, formData.sieve_weight_retained_20mm, formData.sieve_weight_retained_12_5mm,
      formData.sieve_weight_retained_10mm, formData.sieve_weight_retained_4_75mm, formData.sieve_weight_retained_2_36mm,
      formData.sieve_weight_retained_1_18mm, formData.sieve_weight_retained_600um, formData.sieve_weight_retained_300um,
      formData.sieve_weight_retained_150um, formData.sieve_weight_retained_pan,
      formData.crushing_weight_sample_1, formData.crushing_weight_sample_2,
      formData.crushing_weight_passing_1, formData.crushing_weight_passing_2,
      formData.flakiness_weight_sample_1, formData.flakiness_weight_sample_2, formData.flakiness_weight_sample_3,
      formData.flakiness_weight_flaky_1, formData.flakiness_weight_flaky_2, formData.flakiness_weight_flaky_3,
      formData.elongation_weight_elongated_1, formData.elongation_weight_elongated_2, formData.elongation_weight_elongated_3,
      selectedTests]);

  const handleReviewerChange = (e) => {
    const selectedOption = e.target.options[e.target.selectedIndex];
    if (selectedOption.value) {
      const reviewerData = {
        name: selectedOption.getAttribute('data-name'),
        designation: selectedOption.getAttribute('data-designation'),
        graduation: selectedOption.getAttribute('data-graduation')
      };
      
      setReviewerInfo(reviewerData);
      
      // Update form data with reviewer information
      setFormData(prev => ({
        ...prev,
        reviewed_by: reviewerData.name,
        reviewed_by_designation: reviewerData.designation,
        reviewed_by_graduation: reviewerData.graduation
      }));
    }
  };

  // Fill Random Data functionality
  const fillRandomData = () => {
    const rand = (min, max, d = 2) => (Math.random() * (max - min) + min).toFixed(d);
    const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    const today = new Date();
    const randomDate = new Date(today.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000);
    const formatDate = (date) => date.toISOString().slice(0, 10);
    
    // Get random customer if available
    const randomCustomer = customers.length > 0 ? customers[randInt(0, customers.length - 1)] : null;
    const customerName = randomCustomer ? (randomCustomer.name || randomCustomer.client_name || `${randomCustomer.first_name || ''} ${randomCustomer.last_name || ''}`.trim()) : 'Test Customer';
    
    // Preserve current sample_description if already set, otherwise use random
    const currentSampleDesc = formData.sample_description;
    let sampleDescriptionToUse = currentSampleDesc;
    
    // Only set random sample description if none is currently selected
    if (!currentSampleDesc || currentSampleDesc === '') {
    const sampleDescriptions = ['10 MM', '20 MM', 'Psand', 'Msand'];
      sampleDescriptionToUse = sampleDescriptions[randInt(0, sampleDescriptions.length - 1)];
    }
    
    setFormData(prev => ({
      ...prev,
      // General Information
      customer_id: randomCustomer ? randomCustomer.id : '',
      customer_name: customerName,
      customer_site_address: 'Sample Material Description - Coarse Aggregate for Testing Purposes',
      sample_description: sampleDescriptionToUse,
      date_of_material_receipt: formatDate(randomDate),
      date_of_receipt: formatDate(randomDate),
      reference_number: `REF-${randInt(1000, 9999)}`,
      date_of_report: formatDate(today),
      ulr_number: `ULR-${randInt(10000, 99999)}`,
      url_number: `ULR-${randInt(10000, 99999)}`,
      job_code_number: `JOB-${randInt(1000, 9999)}`,
      period_of_testing: `${formatDate(randomDate)} to ${formatDate(today)}`,
      condition_of_sample: 'Acceptable', // Constant value
      location_of_testing: 'Lab', // Constant value
      location: 'Mumbai, Maharashtra',
      source_of_material: 'Quarry Location - Sample Supplier',
      test_method: 'IS 2386 (Part 1)',
      environmental_conditions: 'Laboratory Conditions - Temperature: 27°C, Humidity: 65%',
      
      // Form-1: Bulk Density
      proposed_use: 'Concrete Construction',
      sample_test_code: `CA-${randInt(2024001, 2024999)}`,
      date_of_testing: formatDate(today),
      total_weight: rand(5000, 10000, 0),
      // Form-1: Sieve Analysis Table
      sieve_weight_retained_40mm: rand(0, 500, 2),
      sieve_percent_weight_retained_40mm: rand(0, 10, 2),
      sieve_cumulative_percent_weight_retained_40mm: rand(0, 10, 2),
      sieve_percent_passing_40mm: rand(90, 100, 2),
      sieve_weight_retained_20mm: rand(500, 2000, 2),
      sieve_percent_weight_retained_20mm: rand(10, 40, 2),
      sieve_cumulative_percent_weight_retained_20mm: rand(10, 50, 2),
      sieve_percent_passing_20mm: rand(50, 90, 2),
      sieve_weight_retained_12_5mm: rand(0, 300, 2),
      sieve_percent_weight_retained_12_5mm: rand(0, 5, 2),
      sieve_cumulative_percent_weight_retained_12_5mm: rand(0, 5, 2),
      sieve_percent_passing_12_5mm: rand(95, 100, 2),
      sieve_weight_retained_10mm: rand(2000, 4000, 2),
      sieve_percent_weight_retained_10mm: rand(30, 60, 2),
      sieve_cumulative_percent_weight_retained_10mm: rand(40, 80, 2),
      sieve_percent_passing_10mm: rand(20, 60, 2),
      sieve_weight_retained_4_75mm: rand(1000, 2000, 2),
      sieve_percent_weight_retained_4_75mm: rand(15, 30, 2),
      sieve_cumulative_percent_weight_retained_4_75mm: rand(70, 95, 2),
      sieve_percent_passing_4_75mm: rand(5, 30, 2),
      sieve_weight_retained_2_36mm: rand(200, 800, 2),
      sieve_percent_weight_retained_2_36mm: rand(2, 15, 2),
      sieve_cumulative_percent_weight_retained_2_36mm: rand(90, 100, 2),
      sieve_percent_passing_2_36mm: rand(0, 10, 2),
      sieve_weight_retained_1_18mm: rand(100, 500, 2),
      sieve_percent_weight_retained_1_18mm: rand(2, 10, 2),
      sieve_cumulative_percent_weight_retained_1_18mm: rand(92, 100, 2),
      sieve_percent_passing_1_18mm: rand(0, 8, 2),
      sieve_weight_retained_600um: rand(50, 300, 2),
      sieve_percent_weight_retained_600um: rand(1, 8, 2),
      sieve_cumulative_percent_weight_retained_600um: rand(93, 100, 2),
      sieve_percent_passing_600um: rand(0, 7, 2),
      sieve_weight_retained_300um: rand(20, 150, 2),
      sieve_percent_weight_retained_300um: rand(0, 5, 2),
      sieve_cumulative_percent_weight_retained_300um: rand(95, 100, 2),
      sieve_percent_passing_300um: rand(0, 5, 2),
      sieve_weight_retained_150um: rand(10, 80, 2),
      sieve_percent_weight_retained_150um: rand(0, 3, 2),
      sieve_cumulative_percent_weight_retained_150um: rand(97, 100, 2),
      sieve_percent_passing_150um: rand(0, 3, 2),
      sieve_weight_retained_pan: rand(50, 300, 2),
      sieve_percent_weight_retained_pan: rand(0, 5, 2),
      sieve_cumulative_percent_weight_retained_pan: rand(95, 100, 2),
      sieve_percent_passing_pan: rand(0, 5, 2),
      volume_measure_cylinder_1: rand(5, 10, 3),
      net_weight_rodded_1: rand(8, 15, 3),
      volume_measure_cylinder_2: rand(5, 10, 3),
      net_weight_rodded_2: rand(8, 15, 3),
      volume_measure_cylinder_3: rand(5, 10, 3),
      net_weight_rodded_3: rand(8, 15, 3),
      net_weight_loose_1: rand(7, 14, 3),
      net_weight_loose_2: rand(7, 14, 3),
      net_weight_loose_3: rand(7, 14, 3),
      
      // Form-2: Sieve Analysis & Crushing Value
      type_size_aggregate_2: '10/20mm Coarse Aggregate',
      total_load_10_minutes: '40 kN',
      crushing_weight_sample_1: rand(2000, 5000, 2),
      crushing_weight_sample_2: rand(2000, 5000, 2),
      crushing_weight_passing_1: rand(200, 1000, 2),
      crushing_weight_passing_2: rand(200, 1000, 2),
      crushing_value_1: rand(15, 35, 2),
      crushing_value_2: rand(15, 35, 2),
      mean_crushing_value: rand(20, 30, 2),
      sieve_40mm_1: rand(0, 5, 2),
      sieve_40mm_2: rand(0, 5, 2),
      sieve_40mm_3: rand(0, 5, 2),
      sieve_20mm_1: rand(10, 30, 2),
      sieve_20mm_2: rand(10, 30, 2),
      sieve_20mm_3: rand(10, 30, 2),
      sieve_10mm_1: rand(30, 60, 2),
      sieve_10mm_2: rand(30, 60, 2),
      sieve_10mm_3: rand(30, 60, 2),
      sieve_4_75mm_1: rand(70, 95, 2),
      sieve_4_75mm_2: rand(70, 95, 2),
      sieve_4_75mm_3: rand(70, 95, 2),
      sieve_2_36mm_1: rand(90, 100, 2),
      sieve_2_36mm_2: rand(90, 100, 2),
      sieve_2_36mm_3: rand(90, 100, 2),
      zone_i_1: rand(0, 10, 2),
      zone_i_2: rand(0, 10, 2),
      zone_i_3: rand(0, 10, 2),
      zone_ii_1: rand(10, 30, 2),
      zone_ii_2: rand(10, 30, 2),
      zone_ii_3: rand(10, 30, 2),
      zone_iii_1: rand(30, 60, 2),
      zone_iii_2: rand(30, 60, 2),
      zone_iii_3: rand(30, 60, 2),
      zone_iv_1: rand(60, 100, 2),
      zone_iv_2: rand(60, 100, 2),
      zone_iv_3: rand(60, 100, 2),
      sum_cumulative_weight_retained: rand(200, 400, 2),
      fineness_modulus: rand(5.0, 7.5, 2),
      
      // Form-3: Specific Gravity
      type_of_aggregate_3: 'Coarse Aggregate',
      max_nominal_size_3: '20mm',
      wt_saturated_surface_dry_1: rand(500, 1000, 2),
      wt_pycnometer_aggregate_water_1: rand(1500, 2000, 2),
      wt_pycnometer_water_1: rand(1000, 1500, 2),
      wt_oven_dried_1: rand(480, 980, 2),
      wt_saturated_surface_dry_2: rand(500, 1000, 2),
      wt_pycnometer_aggregate_water_2: rand(1500, 2000, 2),
      wt_pycnometer_water_2: rand(1000, 1500, 2),
      wt_oven_dried_2: rand(480, 980, 2),
      wt_saturated_surface_dry_3: rand(500, 1000, 2),
      wt_pycnometer_aggregate_water_3: rand(1500, 2000, 2),
      wt_pycnometer_water_3: rand(1000, 1500, 2),
      wt_oven_dried_3: rand(480, 980, 2),
      
      // Form-4: Flakiness & Elongation
      type_of_aggregate_4: 'Coarse Aggregate',
      max_nominal_size_4: '20mm',
      flakiness_10mm_1: rand(5, 25, 2),
      flakiness_10mm_2: rand(5, 25, 2),
      flakiness_10mm_3: rand(5, 25, 2),
      flakiness_20mm_1: rand(5, 25, 2),
      flakiness_20mm_2: rand(5, 25, 2),
      flakiness_20mm_3: rand(5, 25, 2),
      elongation_10mm_1: rand(10, 35, 2),
      elongation_10mm_2: rand(10, 35, 2),
      elongation_10mm_3: rand(10, 35, 2),
      elongation_20mm_1: rand(10, 35, 2),
      elongation_20mm_2: rand(10, 35, 2),
      elongation_20mm_3: rand(10, 35, 2),
      // Form-4: Flakiness & Elongation Table Fields
      flakiness_weight_sample_1: rand(2000, 5000, 2),
      flakiness_weight_sample_2: rand(2000, 5000, 2),
      flakiness_weight_sample_3: rand(2000, 5000, 2),
      flakiness_weight_flaky_1: rand(200, 1000, 2),
      flakiness_weight_flaky_2: rand(200, 1000, 2),
      flakiness_weight_flaky_3: rand(200, 1000, 2),
      flakiness_index_1: rand(5, 25, 2),
      flakiness_index_2: rand(5, 25, 2),
      flakiness_index_3: rand(5, 25, 2),
      mean_flakiness_index: rand(10, 20, 2),
      elongation_weight_elongated_1: rand(300, 1500, 2),
      elongation_weight_elongated_2: rand(300, 1500, 2),
      elongation_weight_elongated_3: rand(300, 1500, 2),
      elongation_index_1: rand(10, 35, 2),
      elongation_index_2: rand(10, 35, 2),
      elongation_index_3: rand(10, 35, 2),
      
      // Form-5: Impact Value
      type_of_aggregate_6: 'Coarse Aggregate',
      max_nominal_size_6: '12.5mm',
      impact_weight_1: rand(3000, 4000, 0),
      impact_weight_2: rand(3000, 4000, 0),
      impact_weight_3: rand(3000, 4000, 0),
      impact_passing_1: rand(200, 800, 0),
      impact_passing_2: rand(200, 800, 0),
      impact_passing_3: rand(200, 800, 0),
      impact_retained_1: rand(2500, 3500, 0),
      impact_retained_2: rand(2500, 3500, 0),
      impact_retained_3: rand(2500, 3500, 0),
      impact_total_weight_1: rand(2700, 4300, 0),
      impact_total_weight_2: rand(2700, 4300, 0),
      impact_total_weight_3: rand(2700, 4300, 0),
      impact_value_1: rand(5, 25, 2),
      impact_value_2: rand(5, 25, 2),
      impact_value_3: rand(5, 25, 2),
      mean_impact_value: rand(10, 20, 2),
      
      // Form-6: Crushing Value
      type_size_aggregate_5: '10/20mm Coarse Aggregate',
      condition_of_aggregate: 'Oven Dried',
      volume_01: rand(5, 10, 3),
      volume_02: rand(5, 10, 3),
      volume_03: rand(5, 10, 3),
      rodded_weight_01: rand(8, 15, 3),
      rodded_weight_02: rand(8, 15, 3),
      rodded_weight_03: rand(8, 15, 3),
      loose_weight_01: rand(7, 14, 3),
      loose_weight_02: rand(7, 14, 3),
      loose_weight_03: rand(7, 14, 3),
      weight_sample_taken_1: rand(2500, 3000, 0),
      weight_sample_taken_2: rand(2500, 3000, 0),
      weight_sample_taken_3: rand(2500, 3000, 0),
      weight_passing_2_36mm_1: rand(200, 600, 0),
      weight_passing_2_36mm_2: rand(200, 600, 0),
      weight_passing_2_36mm_3: rand(200, 600, 0),
      
      // Verification
      tested_by_name: 'Demo User',
      tested_by_date: formatDate(today),
      checked_by_name: 'Quality Manager',
      checked_by_date: formatDate(today),
      verified_by_name: reviewerInfo.name,
      verified_by_date: formatDate(today),
      remarks: 'Random test data generated for testing purposes.'
    }));
    
    // Trigger calculations after filling random data
    setTimeout(() => {
      if (selectedTests.includes('specificGravity')) {
        calculateSpecificGravity();
      }
      if (selectedTests.includes('impactValue')) {
        calculateImpactValue();
      }
      if (selectedTests.includes('bulkDensity')) {
        calculateBulkDensity();
      }
    }, 100);
    
    // Update customer search term
    if (randomCustomer) {
      setCustomerSearchTerm(customerName);
    }
  };

  // Auto-calculation functions for Form-5
  const calculateBulkDensity = (fieldName = null, value = null) => {
    // If no parameters provided, calculate all bulk density values
    if (!fieldName || !value) {
      // Calculate all bulk density values
      for (let i = 1; i <= 3; i++) {
        const volume = parseFloat(formData[`volume_measure_cylinder_${i}`] || 0);
        const roddedWeight = parseFloat(formData[`net_weight_rodded_${i}`] || 0);
        const looseWeight = parseFloat(formData[`net_weight_loose_${i}`] || 0);
        
        if (volume > 0 && roddedWeight > 0) {
          const roddedDensity = roddedWeight / volume;
          setFormData(prev => ({
            ...prev,
            [`rodded_bulk_density_${i}`]: roddedDensity.toFixed(3)
          }));
        }
        
        if (volume > 0 && looseWeight > 0) {
          const looseDensity = looseWeight / volume;
          setFormData(prev => ({
            ...prev,
            [`loose_bulk_density_${i}`]: looseDensity.toFixed(3)
          }));
        }
      }
      
      // Calculate mean values
      const roddedDensities = [1, 2, 3].map(i => parseFloat(formData[`rodded_bulk_density_${i}`]) || 0).filter(d => d > 0);
      const looseDensities = [1, 2, 3].map(i => parseFloat(formData[`loose_bulk_density_${i}`]) || 0).filter(d => d > 0);
      
      if (roddedDensities.length > 0) {
        const meanRodded = roddedDensities.reduce((sum, d) => sum + d, 0) / roddedDensities.length;
        // Convert from kg/lit to kg/m³ (multiply by 1000)
        const meanRoddedKgPerM3 = meanRodded * 1000;
        setFormData(prev => ({
          ...prev,
          mean_rodded_bulk_density: meanRoddedKgPerM3.toFixed(2)
        }));
      }
      
      if (looseDensities.length > 0) {
        const meanLoose = looseDensities.reduce((sum, d) => sum + d, 0) / looseDensities.length;
        // Convert from kg/lit to kg/m³ (multiply by 1000)
        const meanLooseKgPerM3 = meanLoose * 1000;
        setFormData(prev => ({
          ...prev,
          mean_loose_bulk_density: meanLooseKgPerM3.toFixed(2)
        }));
      }
      return;
    }
    
    if (fieldName.includes('volume_measure_cylinder_') || fieldName.includes('net_weight_rodded_') || fieldName.includes('net_weight_loose_')) {
      const testNumber = fieldName.split('_').pop();
      
      // Get current values
      const volume = parseFloat(formData[`volume_measure_cylinder_${testNumber}`] || 0);
      const roddedWeight = parseFloat(formData[`net_weight_rodded_${testNumber}`] || 0);
      const looseWeight = parseFloat(formData[`net_weight_loose_${testNumber}`] || 0);
      
      // Calculate rodded density
      if (volume > 0 && roddedWeight > 0) {
        const roddedDensity = roddedWeight / volume;
        setFormData(prev => ({
          ...prev,
          [`rodded_bulk_density_${testNumber}`]: roddedDensity.toFixed(3)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [`rodded_bulk_density_${testNumber}`]: ''
        }));
      }
      
      // Calculate loose density
      if (volume > 0 && looseWeight > 0) {
        const looseDensity = looseWeight / volume;
        setFormData(prev => ({
          ...prev,
          [`loose_bulk_density_${testNumber}`]: looseDensity.toFixed(3)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [`loose_bulk_density_${testNumber}`]: ''
        }));
      }
      
      // Calculate mean values
      calculateMeanBulkDensityValues();
    }
  };

  const calculateMeanBulkDensityValues = () => {
    // Calculate mean rodded density
    const roddedValues = [
      parseFloat(formData.rodded_bulk_density_1) || 0,
      parseFloat(formData.rodded_bulk_density_2) || 0,
      parseFloat(formData.rodded_bulk_density_3) || 0
    ].filter(val => val > 0);
    
    if (roddedValues.length > 0) {
      const meanRodded = roddedValues.reduce((sum, val) => sum + val, 0) / roddedValues.length;
      // Convert from kg/lit to kg/m³ (multiply by 1000)
      const meanRoddedKgPerM3 = meanRodded * 1000;
      setFormData(prev => ({
        ...prev,
        mean_rodded_bulk_density: meanRoddedKgPerM3.toFixed(2)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        mean_rodded_bulk_density: ''
      }));
    }
    
    // Calculate mean loose density
    const looseValues = [
      parseFloat(formData.loose_bulk_density_1) || 0,
      parseFloat(formData.loose_bulk_density_2) || 0,
      parseFloat(formData.loose_bulk_density_3) || 0
    ].filter(val => val > 0);
    
    if (looseValues.length > 0) {
      const meanLoose = looseValues.reduce((sum, val) => sum + val, 0) / looseValues.length;
      // Convert from kg/lit to kg/m³ (multiply by 1000)
      const meanLooseKgPerM3 = meanLoose * 1000;
      setFormData(prev => ({
        ...prev,
        mean_loose_bulk_density: meanLooseKgPerM3.toFixed(2)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        mean_loose_bulk_density: ''
      }));
    }
  };

  // Calculate Specific Gravity and Water Absorption
  const calculateSpecificGravity = () => {
    for (let i = 1; i <= 3; i++) {
      const A = parseFloat(formData[`wt_pycnometer_aggregate_water_${i}`] || 0); // Weight of Aggregate & Vessel and water
      const B = parseFloat(formData[`wt_pycnometer_water_${i}`] || 0); // Weight of Empty Vessel and Water
      const C = parseFloat(formData[`wt_saturated_surface_dry_${i}`] || 0); // Weight of Saturated Surface Dry
      const D = parseFloat(formData[`wt_oven_dried_${i}`] || 0); // Weight of Oven Dried
      
      // Calculate Specific Gravity = D/(D-(A-B))
      if (A > 0 && B > 0 && D > 0) {
        const denominator = D - (A - B);
        if (denominator !== 0) {
          const specificGravity = D / denominator;
          setFormData(prev => ({
            ...prev,
            [`specific_gravity_${i}`]: specificGravity.toFixed(3)
          }));
        }
      }
      
      // Calculate Water Absorption = [(C-D)/D]*100
      if (C > 0 && D > 0) {
        const waterAbsorption = ((C - D) / D) * 100;
        setFormData(prev => ({
          ...prev,
          [`water_absorption_${i}`]: waterAbsorption.toFixed(2)
        }));
      }
    }
    
    // Calculate Mean Specific Gravity
    const specificGravityValues = [1, 2, 3]
      .map(i => parseFloat(formData[`specific_gravity_${i}`]) || 0)
      .filter(val => val > 0);
    
    if (specificGravityValues.length > 0) {
      const meanSpecificGravity = specificGravityValues.reduce((sum, val) => sum + val, 0) / specificGravityValues.length;
      setFormData(prev => ({
        ...prev,
        mean_specific_gravity: meanSpecificGravity.toFixed(3)
      }));
    }
    
    // Calculate Mean Water Absorption
    const waterAbsorptionValues = [1, 2, 3]
      .map(i => parseFloat(formData[`water_absorption_${i}`]) || 0)
      .filter(val => val >= 0);
    
    if (waterAbsorptionValues.length > 0) {
      const meanWaterAbsorption = waterAbsorptionValues.reduce((sum, val) => sum + val, 0) / waterAbsorptionValues.length;
      setFormData(prev => ({
        ...prev,
        mean_water_absorption: meanWaterAbsorption.toFixed(2)
      }));
    }
  };

  // Calculate Impact Value
  const calculateImpactValue = () => {
    for (let i = 1; i <= 3; i++) {
      const A = parseFloat(formData[`impact_weight_${i}`] || 0); // Weight of Sample taken
      const B = parseFloat(formData[`impact_passing_${i}`] || 0); // Weight passing through 2.36mm
      const C = parseFloat(formData[`impact_retained_${i}`] || 0); // Weight retained on 2.36mm
      
      // Calculate Total Weight (B+C) - this is usually auto-calculated but we can store it
      if (B > 0 && C > 0) {
        const totalWeight = B + C;
        setFormData(prev => ({
          ...prev,
          [`impact_total_weight_${i}`]: totalWeight.toFixed(2)
        }));
      }
      
      // Calculate Impact Value = (B/A)*100
      if (A > 0 && B > 0) {
        const impactValue = (B / A) * 100;
        setFormData(prev => ({
          ...prev,
          [`impact_value_${i}`]: impactValue.toFixed(2)
        }));
      }
    }
    
    // Calculate Mean Impact Value
    const impactValues = [1, 2, 3]
      .map(i => parseFloat(formData[`impact_value_${i}`]) || 0)
      .filter(val => val > 0);
    
    if (impactValues.length > 0) {
      const meanImpactValue = impactValues.reduce((sum, val) => sum + val, 0) / impactValues.length;
      setFormData(prev => ({
        ...prev,
        mean_impact_value: meanImpactValue.toFixed(2)
      }));
    }
  };

  // Calculate Sieve Analysis
  // B = Weight Retained (gm) = input
  // C = % Weight Retained = B / (sum of B) × 100
  // D = Cumulative % Weight Retained: D1 = C1, D2 = C1+C2, D3 = C1+C2+C3, Dn = C1+...+Cn
  // E = % Passing = 100 - D: E1 = 100-D1, E2 = 100-D2, En = 100-Dn
  const calculateSieveAnalysis = () => {
    // Define all sieve sizes in order
    const sieveSizes = ['40mm', '20mm', '12_5mm', '10mm', '4_75mm', '2_36mm', '1_18mm', '600um', '300um', '150um', 'pan'];
    
    // Step 1: Calculate Sum of all B (Weight Retained)
    let sumOfB = 0;
    const B_values = [];
    sieveSizes.forEach(size => {
      const B = parseFloat(formData[`sieve_weight_retained_${size}`] || 0);
      B_values.push(B);
      sumOfB += B;
    });
    
    // If sum is zero, don't calculate
    if (sumOfB <= 0) {
      return;
    }
    
    // Step 2 & 3: Calculate C (% Weight Retained) and D (Cumulative % Weight Retained)
    let cumulativeSum = 0;
    let sumCumulativePercentRetained = 0; // For Sum of Cumulative % Weight Retained (excluding Pan)
    const D_values = {}; // Store D values for fineness modulus calculation
    
    sieveSizes.forEach((size, index) => {
      const B = B_values[index];
      
      // Step 2: Calculate C = B / (sum of B) × 100
      const C = (B / sumOfB) * 100;
      
      // Step 3: Calculate D (Cumulative % Weight Retained)
      // D1 = C1, D2 = C1 + C2, D3 = C1 + C2 + C3, etc.
      cumulativeSum += C;
      const D = cumulativeSum;
      D_values[size] = D; // Store D value for later use
      
      // Step 4: Calculate E (% Passing) = 100 - D
      const E = 100 - D;
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        [`sieve_percent_weight_retained_${size}`]: C.toFixed(2),
        [`sieve_cumulative_percent_weight_retained_${size}`]: D.toFixed(2),
        [`sieve_percent_passing_${size}`]: E.toFixed(2)
      }));
      
      // For Sum of Cumulative % Weight Retained (excluding Pan)
      if (size !== 'pan') {
        sumCumulativePercentRetained += D;
      }
    });
    
    // Step 5: Calculate Fineness Modulus
    // Formula: (Sum of Cumulative % weight retained/100) + 3
    // Standard sieves: 150μm, 300μm, 600μm, 1.18mm, 2.36mm, 4.75mm, 10mm, 20mm, 40mm (excluding pan)
    const standardSieves = ['150um', '300um', '600um', '1_18mm', '2_36mm', '4_75mm', '10mm', '20mm', '40mm'];
    const sumCumulativeForFM = standardSieves.reduce((sum, size) => {
      const D = D_values[size] || 0;
      return sum + D;
    }, 0);
    
    const finenessModulus = (sumCumulativeForFM / 100) + 3;
    
    // Update form data with final calculated values
    setFormData(prev => ({
      ...prev,
      sum_cumulative_weight_retained: sumCumulativePercentRetained.toFixed(2),
      fineness_modulus: finenessModulus.toFixed(2)
    }));
  };

  // Calculate Crushing Value
  // A = Weight of sample material taken (gm) = input
  // B = Weight of fraction of sample passing through IS Sieve 2.36mm (gm) = input
  // Formula: Aggregate Crushing Value = (B/A) × 100
  // Mean Aggregate Crushing Value = average of all crushing values
  const calculateCrushingValue = () => {
    // Calculate for test 1
    const A1 = parseFloat(formData.crushing_weight_sample_1 || 0); // Weight of sample material taken
    const B1 = parseFloat(formData.crushing_weight_passing_1 || 0); // Weight passing through 2.36mm
    
    let crushingValue1 = '';
    if (A1 > 0 && B1 > 0) {
      crushingValue1 = ((B1 / A1) * 100).toFixed(2);
    }
    
    // Calculate for test 2
    const A2 = parseFloat(formData.crushing_weight_sample_2 || 0); // Weight of sample material taken
    const B2 = parseFloat(formData.crushing_weight_passing_2 || 0); // Weight passing through 2.36mm
    
    let crushingValue2 = '';
    if (A2 > 0 && B2 > 0) {
      crushingValue2 = ((B2 / A2) * 100).toFixed(2);
    }
    
    // Calculate Mean Aggregate Crushing Value = average of all crushing values
    const crushingValues = [
      parseFloat(crushingValue1) || 0,
      parseFloat(crushingValue2) || 0
    ].filter(val => val > 0);
    
    let meanCrushingValue = '';
    if (crushingValues.length > 0) {
      meanCrushingValue = (crushingValues.reduce((sum, val) => sum + val, 0) / crushingValues.length).toFixed(2);
    }
    
    // Update all values at once
    setFormData(prev => ({
      ...prev,
      crushing_value_1: crushingValue1,
      crushing_value_2: crushingValue2,
      mean_crushing_value: meanCrushingValue
    }));
  };

  // Calculate Flakiness Index
  // A = Weight of Sample taken (gm) = input
  // B = Weight of Flaky Material (gm) = input
  // Formula: Flakiness Index = (B/A) × 100
  // Mean Flakiness Index = average of all flakiness indices
  const calculateFlakinessIndex = () => {
    let flakinessIndex1 = '';
    let flakinessIndex2 = '';
    let flakinessIndex3 = '';
    
    // Calculate for test 1
    const A1 = parseFloat(formData.flakiness_weight_sample_1 || 0); // Weight of Sample taken
    const B1 = parseFloat(formData.flakiness_weight_flaky_1 || 0); // Weight of Flaky Material
    
    if (A1 > 0 && B1 > 0) {
      flakinessIndex1 = ((B1 / A1) * 100).toFixed(2);
    }
    
    // Calculate for test 2
    const A2 = parseFloat(formData.flakiness_weight_sample_2 || 0);
    const B2 = parseFloat(formData.flakiness_weight_flaky_2 || 0);
    
    if (A2 > 0 && B2 > 0) {
      flakinessIndex2 = ((B2 / A2) * 100).toFixed(2);
    }
    
    // Calculate for test 3
    const A3 = parseFloat(formData.flakiness_weight_sample_3 || 0);
    const B3 = parseFloat(formData.flakiness_weight_flaky_3 || 0);
    
    if (A3 > 0 && B3 > 0) {
      flakinessIndex3 = ((B3 / A3) * 100).toFixed(2);
    }
    
    // Calculate Mean Flakiness Index = average of all flakiness indices
    const flakinessValues = [
      parseFloat(flakinessIndex1) || 0,
      parseFloat(flakinessIndex2) || 0,
      parseFloat(flakinessIndex3) || 0
    ].filter(val => val > 0);
    
    let meanFlakinessIndex = '';
    if (flakinessValues.length > 0) {
      meanFlakinessIndex = (flakinessValues.reduce((sum, val) => sum + val, 0) / flakinessValues.length).toFixed(2);
    }
    
    // Update all values at once
    setFormData(prev => ({
      ...prev,
      flakiness_index_1: flakinessIndex1,
      flakiness_index_2: flakinessIndex2,
      flakiness_index_3: flakinessIndex3,
      mean_flakiness_index: meanFlakinessIndex
    }));
  };

  // Calculate Elongation Index
  // A = Weight of Sample taken (gm) = input (same as flakiness)
  // C = Weight of Elongated Material (gm) = input
  // Formula: Elongation Index = (C/A) × 100
  // Mean Elongation Index = average of all elongation indices
  const calculateElongationIndex = () => {
    let elongationIndex1 = '';
    let elongationIndex2 = '';
    let elongationIndex3 = '';
    
    // Calculate for test 1
    const A1 = parseFloat(formData.flakiness_weight_sample_1 || 0); // Weight of Sample taken (same as flakiness)
    const C1 = parseFloat(formData.elongation_weight_elongated_1 || 0); // Weight of Elongated Material
    
    if (A1 > 0 && C1 > 0) {
      elongationIndex1 = ((C1 / A1) * 100).toFixed(2);
    }
    
    // Calculate for test 2
    const A2 = parseFloat(formData.flakiness_weight_sample_2 || 0);
    const C2 = parseFloat(formData.elongation_weight_elongated_2 || 0);
    
    if (A2 > 0 && C2 > 0) {
      elongationIndex2 = ((C2 / A2) * 100).toFixed(2);
    }
    
    // Calculate for test 3
    const A3 = parseFloat(formData.flakiness_weight_sample_3 || 0);
    const C3 = parseFloat(formData.elongation_weight_elongated_3 || 0);
    
    if (A3 > 0 && C3 > 0) {
      elongationIndex3 = ((C3 / A3) * 100).toFixed(2);
    }
    
    // Calculate Mean Elongation Index = average of all elongation indices
    const elongationValues = [
      parseFloat(elongationIndex1) || 0,
      parseFloat(elongationIndex2) || 0,
      parseFloat(elongationIndex3) || 0
    ].filter(val => val > 0);
    
    let meanElongationIndex = '';
    if (elongationValues.length > 0) {
      meanElongationIndex = (elongationValues.reduce((sum, val) => sum + val, 0) / elongationValues.length).toFixed(2);
    }
    
    // Update all values at once
    setFormData(prev => ({
      ...prev,
      elongation_index_1: elongationIndex1,
      elongation_index_2: elongationIndex2,
      elongation_index_3: elongationIndex3,
      mean_elongation_index: meanElongationIndex
    }));
  };





  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Validate customer is selected
      if (!formData.customer_id && !formData.customer_name) {
        setSubmitError('Please select a customer');
        setIsSubmitting(false);
        return;
      }

      // Create test request first (using separate Coarse Aggregate backend)
      const testRequestData = {
        customer_id: formData.customer_id,
        customer_name: formData.customer_name,
        receipt_date: formData.date_of_material_receipt || formData.date_of_receipt || new Date().toISOString().split('T')[0],
        site_name: formData.customer_site_address || '',
        reference_number: formData.reference_number || '',
        job_number: formData.job_code_number || '',
        ulr_number: formData.url_number || formData.ulr_number || ''
      };

      let finalTestRequestId = null;
      
      // Update existing test request if in edit mode
      if (isEditMode && testRequestId) {
        console.log('🔄 [COARSE AGGREGATE FORM] Updating test request ID:', testRequestId);
        const updateResponse = await updateCoarseAggregateTestRequest(testRequestId, testRequestData);
        finalTestRequestId = updateResponse.test_request_id || updateResponse.id || testRequestId;
        console.log('✅ [COARSE AGGREGATE FORM] Test request updated:', finalTestRequestId);
      } else {
        // Create new test request
        console.log('Creating Coarse Aggregate test request:', testRequestData);
        const testRequestResponse = await createCoarseAggregateTestRequest(testRequestData);
        console.log('🔵 [COARSE AGGREGATE FORM] Test request response:', testRequestResponse);
        // Try multiple possible response field names
        finalTestRequestId = testRequestResponse.test_request_id || 
                            testRequestResponse.id || 
                            testRequestResponse.testRequestId ||
                            (testRequestResponse.data && testRequestResponse.data.id) ||
                            (testRequestResponse.data && testRequestResponse.data.test_request_id);
        console.log('🔵 [COARSE AGGREGATE FORM] Extracted test request ID:', finalTestRequestId);
        
        if (!finalTestRequestId) {
          throw new Error('Failed to get test request ID from server response');
        }
        
        setTestRequestId(finalTestRequestId);
      }

      // Save observations (using separate Coarse Aggregate backend)
      const observationsData = {
        formData: formData,
        selectedTests: selectedTests,
        tested_by_name: formData.tested_by_name || '',
        tested_by_date: formData.tested_by_date || '',
        checked_by_name: formData.checked_by_name || '',
        checked_by_date: formData.checked_by_date || '',
        verified_by_name: formData.verified_by_name || reviewerInfo.name,
        verified_by_date: formData.verified_by_date || '',
        reviewerInfo: reviewerInfo,
        authorized_by: formData.authorized_by || 'Prakarsh Sangave',
        remarks: formData.remarks || '',
        customer_name: formData.customer_name,
        customer_id: formData.customer_id
      };

      console.log('Saving Coarse Aggregate observations:', observationsData);
      console.log('🔵 [COARSE AGGREGATE FORM] Using test request ID:', finalTestRequestId);
      await saveCoarseAggregateObservations(finalTestRequestId, observationsData);

      // Log form data for debugging
      console.log('📋 Form Submission - Sample Description:', formData.sample_description);
      console.log('📋 Form Submission - Full formData:', formData);

      // Navigate to test results page with selected tests data
      navigate('/test-results', {
        state: {
          selectedTests: selectedTests,
          formData: formData,
          testRequestId: finalTestRequestId,
          testType: 'Coarse Aggregate'
        }
      });
    } catch (error) {
      console.error('Error submitting Coarse Aggregate form:', error);
      setSubmitError(error.message || 'Failed to save test data. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderVerification = () => (
    <Row className="mb-4">
      <Col>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faUserCheck} className="me-2" />
              Verification
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
              <Row>
              <Col md={4}>
                <h6>Tested By</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control type="text" name="tested_by_name" value={formData.tested_by_name} onChange={handleInputChange} placeholder="" required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control type="date" name="tested_by_date" value={formData.tested_by_date} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <h6>Checked By</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control type="text" name="checked_by_name" value={formData.checked_by_name} onChange={handleInputChange} placeholder="" required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control type="date" name="checked_by_date" value={formData.checked_by_date} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <h6>Verified By</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" name="verified_by_name" value={formData.verified_by_name} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control type="date" name="verified_by_date" value={formData.verified_by_date} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderRemarks = () => (
    <Row className="mb-4">
      <Col>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faComment} className="me-2" />
              Remarks
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} name="remarks" value={formData.remarks} onChange={handleInputChange} placeholder="" />
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderReviewedBy = () => (
    <Row className="mb-4">
      <Col md={6}>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faUserCheck} className="me-2" />
              Reviewed By
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
              <Form.Label>Select Reviewer:</Form.Label>
              {loadingReviewers ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading reviewers...</span>
                </div>
              ) : (
                <Form.Select 
                  id="reviewer_select" 
                  onChange={handleReviewerChange}
                  value={reviewerInfo.name}
                  className="form-select"
                  required
                  style={{
                    backgroundColor: '#495057',
                    border: '2px solid #6c757d',
                    borderRadius: '8px',
                    color: 'white',
                    padding: '12px 15px',
                    fontSize: '1rem'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FFA500';
                    e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#6c757d';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {reviewers.map((reviewer) => (
                    <option 
                      key={reviewer.id} 
                      value={reviewer.name} 
                      data-name={reviewer.name} 
                      data-designation={reviewer.designation} 
                      data-graduation={reviewer.graduation}
                      style={{ backgroundColor: '#495057', color: 'white' }}
                    >
                      {reviewer.name} - {reviewer.designation}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6}>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faUserCheck} className="me-2" />
              Authorized By
            </h5>
          </Card.Header>
          <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
              <Form.Label>Authorized By:</Form.Label>
              <Form.Control type="text" name="authorized_by" value={formData.authorized_by} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderGeneralInformation = (formNumber) => {
    const getFormSpecificFields = () => {
      // Common fields for all forms (from TEST REPORT table)
      const commonFields = {
        col1: [
          { name: 'customer_id', label: 'Customer *', type: 'select', required: true },
          { name: 'customer_site_address', label: 'Material Description *', type: 'textarea', required: true, rows: 2 },
          { name: 'sample_description', label: 'Sample Description *', type: 'select', required: true },
          { name: 'date_of_material_receipt', label: 'Date of Material Receipt *', type: 'date', required: true },
          { name: 'reference_number', label: 'Reference Number', type: 'text', placeholder: 'Enter reference number' },
          { name: 'condition_of_sample', label: 'Condition of Sample', type: 'text', placeholder: 'Acceptable (Constant)' },
          { name: 'source_of_material', label: 'Source of Material', type: 'text', placeholder: 'e.g., Quarry Location, Supplier Name' },
          { name: 'location', label: 'Location', type: 'text', placeholder: 'Enter location' }
        ],
        col2: [
          { name: 'date_of_report', label: 'Date of Report *', type: 'date', required: true },
          { name: 'ulr_number', label: 'ULR Number', type: 'text', placeholder: 'Enter ULR number' },
          { name: 'job_code_number', label: 'Job Code Number', type: 'text', placeholder: 'Enter job code number' },
          { name: 'period_of_testing', label: 'Period of Testing', type: 'text', placeholder: 'e.g., 01/01/2024 to 15/01/2024' },
          { name: 'location_of_testing', label: 'Location of Testing', type: 'text', placeholder: 'e.g., Lab' },
          { name: 'test_method', label: 'Test Method', type: 'text', placeholder: 'e.g., IS 2386 (Part 1)' }
        ]
      };

      switch (formNumber) {
        case 1: // Bulk Density
          return {
            col1: [
              ...commonFields.col1,
              { name: 'proposed_use', label: 'Proposed Use', type: 'text', placeholder: 'e.g., Concrete, Road Construction' }
            ],
            col2: [
              ...commonFields.col2,
              { name: 'sample_test_code', label: 'Sample Test Code Number *', type: 'text', required: true, placeholder: 'e.g., CA-2024001' },
              { name: 'date_of_testing', label: 'Date of Testing *', type: 'date', required: true },
              { name: 'environmental_conditions', label: 'Environmental Conditions', type: 'text', placeholder: 'e.g., Laboratory Conditions' }
            ]
          };
        case 2: // Sieve Analysis
          return {
            col1: [
              ...commonFields.col1,
              { name: 'type_size_aggregate_2', label: 'Type & Size of Aggregate', type: 'text', placeholder: 'e.g., 10/20mm Coarse Aggregate' }
            ],
            col2: [
              ...commonFields.col2,
              { name: 'sample_test_code', label: 'Sample Test Code Number *', type: 'text', required: true, placeholder: 'e.g., CA-2024001' },
              { name: 'date_of_testing', label: 'Date of Testing *', type: 'date', required: true },
              { name: 'total_load_10_minutes', label: 'Total load to be applied in 10 minutes', type: 'text', placeholder: 'e.g., 40 kN' },
              { name: 'environmental_conditions', label: 'Environmental Conditions', type: 'text', placeholder: 'e.g., Laboratory Conditions' }
            ]
          };
        case 3: // Specific Gravity & Water Absorption
          return {
            col1: [
              ...commonFields.col1,
              { name: 'type_of_aggregate_3', label: 'Type of Aggregate', type: 'text', placeholder: 'e.g., Crushed Stone' }
            ],
            col2: [
              ...commonFields.col2,
              { name: 'sample_test_code', label: 'Sample Test Code Number *', type: 'text', required: true, placeholder: 'e.g., CA-2024001' },
              { name: 'date_of_testing', label: 'Date of Testing *', type: 'date', required: true },
              { name: 'max_nominal_size_3', label: 'Maximum Nominal Size of Aggregate', type: 'text', placeholder: 'e.g., 20mm' },
              { name: 'environmental_conditions', label: 'Environmental Conditions', type: 'text', placeholder: 'e.g., Laboratory Conditions' }
            ]
          };
        case 4: // Flakiness Index
          return {
            col1: [
              ...commonFields.col1,
              { name: 'type_of_aggregate_4', label: 'Type of Aggregate', type: 'text', placeholder: 'e.g., Crushed Stone' }
            ],
            col2: [
              ...commonFields.col2,
              { name: 'sample_test_code', label: 'Sample Test Code Number *', type: 'text', required: true, placeholder: 'e.g., CA-2024001' },
              { name: 'date_of_testing', label: 'Date of Testing *', type: 'date', required: true },
              { name: 'max_nominal_size_4', label: 'Maximum Nominal Size of Aggregate', type: 'text', placeholder: 'e.g., 20mm' },
              { name: 'environmental_conditions', label: 'Environmental Conditions', type: 'text', placeholder: 'e.g., Laboratory Conditions' }
            ]
          };
        case 5: // Elongation Index
          return {
            col1: [
              ...commonFields.col1,
              { name: 'type_size_aggregate_5', label: 'Type & Size of Aggregate', type: 'text', placeholder: 'e.g., 10/20mm Coarse Aggregate' }
            ],
            col2: [
              ...commonFields.col2,
              { name: 'sample_test_code', label: 'Sample Test Code Number *', type: 'text', required: true, placeholder: 'e.g., CA-2024001' },
              { name: 'date_of_testing', label: 'Date of Testing *', type: 'date', required: true },
              { name: 'condition_of_aggregate', label: 'Condition of Aggregate', type: 'text', placeholder: 'e.g., Dry, Saturated' },
              { name: 'environmental_conditions', label: 'Environmental Conditions', type: 'text', placeholder: 'e.g., Laboratory Conditions' }
            ]
          };
        case 6: // Impact Value
          return {
            col1: [
              ...commonFields.col1,
              { name: 'type_of_aggregate_6', label: 'Type of Aggregate', type: 'text', placeholder: 'e.g., Crushed Stone' }
            ],
            col2: [
              ...commonFields.col2,
              { name: 'sample_test_code', label: 'Sample Test Code Number *', type: 'text', required: true, placeholder: 'e.g., CA-2024001' },
              { name: 'date_of_testing', label: 'Date of Testing *', type: 'date', required: true },
              { name: 'max_nominal_size_6', label: 'Maximum Nominal Size of Aggregate', type: 'text', placeholder: 'e.g., 20mm' },
              { name: 'environmental_conditions', label: 'Environmental Conditions', type: 'text', placeholder: 'e.g., Laboratory Conditions' }
            ]
          };
        default:
          return { col1: [], col2: [] };
      }
    };

    const fields = getFormSpecificFields();

    return (
      <Row className="mb-4">
        <Col>
          <Card className="card-vitrag shadow-sm">
            <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
              <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                General Information
              </h5>
            </Card.Header>
            <Card.Body className="transparent-input-section">
              <Row>
                <Col md={6}>
                  {fields.col1.map((field, index) => (
                    <Form.Group key={index} className="mb-3">
                      <Form.Label>{field.label}</Form.Label>
                      {field.name === 'customer_id' ? (
                        customersLoading ? (
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span style={{ color: 'white' }}>Loading customers...</span>
                          </div>
                        ) : customersError ? (
                          <Alert variant="warning" className="mb-0">
                            {customersError}
                          </Alert>
                        ) : (
                        <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                          <Form.Control
                            type="text"
                            value={customerSearchTerm}
                              onChange={(e) => setCustomerSearchTerm(e.target.value)}
                              onClick={handleCustomerInputClick}
                              placeholder="Search and select customer..."
                            required={field.required}
                              autoComplete="off"
                              readOnly
                              style={{ 
                                cursor: 'pointer',
                                backgroundColor: '#1C2333',
                                border: '2px solid #6c757d',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '12px 15px',
                                fontSize: '1rem'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#FFA500';
                                e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#6c757d';
                                e.target.style.boxShadow = 'none';
                              }}
                          />
                            {showCustomerDropdown && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: '#1C2333',
                                border: '2px solid #6c757d',
                                  borderTop: 'none',
                                  borderRadius: '0 0 8px 8px',
                                  maxHeight: '300px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                {/* Search input inside dropdown */}
                                <div style={{ padding: '15px', borderBottom: '1px solid #6c757d' }}>
                                  <Form.Control
                                    type="text"
                                    placeholder="Search customers..."
                                    value={dropdownSearchTerm}
                                    onChange={handleDropdownSearch}
                                    autoComplete="off"
                                    style={{
                                      backgroundColor: '#242B3C',
                                      border: '2px solid #6c757d',
                                      borderRadius: '8px',
                                      color: 'white',
                                      padding: '10px 12px',
                                      fontSize: '0.9rem'
                              }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#FFA500';
                                      e.target.style.boxShadow = '0 0 0 0.2rem rgba(255, 165, 0, 0.25)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#6c757d';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                </div>
                                
                                {/* Customer list */}
                                {filteredCustomers.length > 0 ? (
                                  filteredCustomers.map((customer) => {
                                const customerName = customer.name || customer.client_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                return (
                                  <div
                                    key={customer.id}
                                    onClick={() => handleCustomerSelect(customer)}
                                    style={{
                                          padding: '10px 15px',
                                      cursor: 'pointer',
                                          borderBottom: '1px solid #6c757d',
                                      color: 'white',
                                          backgroundColor: 'transparent'
                                    }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a3441'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                    {customerName}
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                          {customer.phone && `${customer.phone} • `}
                                          {customer.city && customer.city}
                                        </div>
                                  </div>
                                );
                                  })
                                ) : (
                                  <div
                                    style={{
                                      padding: '15px',
                                      color: '#adb5bd',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {dropdownSearchTerm.trim() !== '' 
                                      ? `No customers found matching "${dropdownSearchTerm}"`
                                      : 'No customers available'
                                    }
                            </div>
                          )}
                            </div>
                          )}
                            </div>
                        )
                      ) : field.name === 'sample_description' ? (
                        <Form.Select
                          key={`sample-description-${currentForm}-${formData.sample_description || 'empty'}`}
                          name={field.name}
                          value={formData[field.name] || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            console.log('📝 Sample Description onChange - Setting to:', newValue);
                            setFormData(prev => {
                              const updated = {
                                ...prev,
                                [field.name]: newValue
                              };
                              console.log('📝 Sample Description - Updated formData:', updated.sample_description);
                              return updated;
                            });
                          }}
                          required={field.required}
                          style={{ backgroundColor: '#1C2333', color: 'white', border: '2px solid #6c757d' }}
                        >
                          <option value="">Select Sample Description *</option>
                          <option value="10 MM">10 MM</option>
                          <option value="20 MM">20 MM</option>
                          <option value="Psand">Psand</option>
                          <option value="Msand">Msand</option>
                        </Form.Select>
                      ) : field.type === 'select' ? (
                        <Form.Select 
                          name={field.name} 
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          required={field.required}
                        >
                          <option value="">Select {field.label}</option>
                        </Form.Select>
                      ) : field.name === 'ulr_number' ? (
                        <Form.Control
                          type={field.type}
                          name="url_number"
                          value={formData.url_number}
                          onChange={handleInputChange}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          required={field.required}
                        />
                      ) : field.name === 'condition_of_sample' ? (
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={formData[field.name] || 'Acceptable'}
                          readOnly
                          style={{ backgroundColor: '#2a3441', color: '#adb5bd', cursor: 'not-allowed' }}
                        />
                      ) : field.type === 'textarea' ? (
                        <Form.Control
                          as="textarea"
                          rows={field.rows || 3}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          required={field.required}
                        />
                      ) : (
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          required={field.required}
                        />
                      )}
                    </Form.Group>
                  ))}
                </Col>
                
                <Col md={6}>
                  {fields.col2.map((field, index) => (
                    <Form.Group key={index} className="mb-3">
                      <Form.Label>{field.label}</Form.Label>
                      {field.name === 'location_of_testing' ? (
                        <Form.Control
                          type="text"
                          name={field.name}
                          value="Lab"
                          readOnly
                          style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }}
                        />
                      ) : (
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          required={field.required}
                        />
                      )}
                    </Form.Group>
                  ))}
                </Col>
              </Row>
              
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };


  const renderCurrentForm = () => {
    const renderFormWithSections = (formNumber, testResultsContent) => {
      // Check if this is the last test
      const isLastTest = currentForm === formTitles.length;
      
      return (
        <>
          {renderGeneralInformation(formNumber)}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Test Results
                  </h5>
                </Card.Header>
                <Card.Body>
                  {testResultsContent}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {/* Only show verification sections on the last test */}
          {isLastTest && (
            <>
              {renderVerification()}
              {renderRemarks()}
              {renderReviewedBy()}
            </>
          )}
        </>
      );
    };

    // Get the current form based on the step number and selected tests
    const currentFormData = formTitles[currentForm - 1];
    if (!currentFormData) {
      return <div>No form data available</div>;
    }

    // Use the testId to determine which form to render
    switch (currentFormData.testId) {
      case 'bulkDensity':
        return renderFormWithSections(1, (
          <div>
            {/* Total Weight Input */}
            <Row className="justify-content-center mb-4">
              <Col md={8}>
                <div className="input-group justify-content-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
                  <span className="input-group-text" style={{ fontWeight: 'bold', backgroundColor: '#495057', color: 'white' }}>
                    Total Weight of Sample taken =
                  </span>
                  <Form.Control 
                    type="number" 
                    step="0.01" 
                    placeholder="" 
                    name="total_weight" 
                    value={formData.total_weight} 
                    onChange={handleInputChange} 
                    className="form-control-vitrag"
                  />
                  <span className="input-group-text" style={{ backgroundColor: '#495057', color: 'white' }}>gm</span>
                </div>
              </Col>
            </Row>
            
            {/* Main Data Table */}
            <div className="table-responsive">
              <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'auto', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '75px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>IS Sieve</th>
                    <th style={{ width: '75px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Weigh. Retained (gm)</th>
                    <th style={{ width: '75px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>% Weight Retained</th>
                    <th style={{ width: '85px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Cumulative % Weight Retained</th>
                    <th style={{ width: '55px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>% Passing</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Get sieve sizes based on sample description
                    const getSieveSizes = () => {
                      const sampleDesc = formData.sample_description || '';
                      
                      if (sampleDesc === '10 MM' || sampleDesc === '10mm') {
                        // For 10mm: 12.5 mm, 10 mm, 4.75 mm, 2.36 mm, Pan
                        return [
                          { label: '12.5 mm', key: '12_5mm' },
                          { label: '10 mm', key: '10mm' },
                          { label: '4.75 mm', key: '4_75mm' },
                          { label: '2.36 mm', key: '2_36mm' },
                          { label: 'Pan', key: 'pan' }
                        ];
                      } else if (sampleDesc === '20 MM' || sampleDesc === '20mm') {
                        // For 20mm: 40 mm, 20 mm, 10 mm, 4.75 mm, Pan
                        return [
                          { label: '40 mm', key: '40mm' },
                          { label: '20 mm', key: '20mm' },
                          { label: '10 mm', key: '10mm' },
                          { label: '4.75 mm', key: '4_75mm' },
                          { label: 'Pan', key: 'pan' }
                        ];
                      } else if (sampleDesc === 'Psand' || sampleDesc === 'Msand' || sampleDesc === 'psand' || sampleDesc === 'msand') {
                        // For Psand/Msand: 10 mm, 4.75 mm, 2.36 mm, 1.18 mm, 600 micron, 300 micron, 150 micron, Pan
                        return [
                          { label: '10 mm', key: '10mm' },
                          { label: '4.75 mm', key: '4_75mm' },
                          { label: '2.36 mm', key: '2_36mm' },
                          { label: '1.18 mm', key: '1_18mm' },
                          { label: '600 micron', key: '600um' },
                          { label: '300 micron', key: '300um' },
                          { label: '150 micron', key: '150um' },
                          { label: 'Pan', key: 'pan' }
                        ];
                      } else {
                        // Default: show all sizes
                        return [
                          { label: '40 mm', key: '40mm' },
                          { label: '20 mm', key: '20mm' },
                          { label: '10 mm', key: '10mm' },
                          { label: '4.75 mm', key: '4_75mm' },
                          { label: '2.36 mm', key: '2_36mm' },
                          { label: 'Pan', key: 'pan' }
                        ];
                      }
                    };
                    
                    return getSieveSizes();
                  })().map((sieve, index) => (
                    <tr key={index} style={{ backgroundColor: '#1C2333' }}>
                      <td style={{ padding: '8px', border: '1px solid #6c757d', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#1C2333', color: 'white' }}>{sieve.label}</td>
                      <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          name={`sieve_weight_retained_${sieve.key}`}
                          value={formData[`sieve_weight_retained_${sieve.key}`] || ''}
                          onChange={handleInputChange}
                          className="form-control form-control-sm" 
                          style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          name={`sieve_percent_weight_retained_${sieve.key}`}
                          value={formData[`sieve_percent_weight_retained_${sieve.key}`] || ''}
                          readOnly
                          className="form-control form-control-sm auto-calculated" 
                          style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          name={`sieve_cumulative_percent_weight_retained_${sieve.key}`}
                          value={formData[`sieve_cumulative_percent_weight_retained_${sieve.key}`] || ''}
                          readOnly
                          className="form-control form-control-sm auto-calculated" 
                          style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                        <Form.Control 
                          type="number" 
                          step="0.01" 
                          name={`sieve_percent_passing_${sieve.key}`}
                          value={formData[`sieve_percent_passing_${sieve.key}`] || ''}
                          readOnly
                          className="form-control form-control-sm auto-calculated" 
                          style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', textAlign: 'center' }}
                        />
                      </td>
                    </tr>
                  ))}
                  
                  {/* Calculation Rows */}
                  <tr style={{ backgroundColor: '#1C2333' }}>
                    <td colSpan="4" style={{ padding: '10px', border: '1px solid #6c757d', fontSize: '1rem', fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle', backgroundColor: '#1C2333', color: 'white' }}>
                      Sum of Cumulative % Weight Retained (Excluding Pan) =
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                      <Form.Control 
                        type="number" 
                        step="0.01" 
                        name="sum_cumulative_weight_retained"
                        value={formData.sum_cumulative_weight_retained || ''}
                        readOnly
                        className="form-control form-control-sm auto-calculated" 
                        style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', textAlign: 'center' }}
                      />
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#1C2333' }}>
                    <td colSpan="4" style={{ padding: '10px', border: '1px solid #6c757d', fontSize: '1rem', fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle', backgroundColor: '#1C2333', color: 'white' }}>
                      Fineness Modulus = (Sum of Cumulative % weight retained/100) + 3 =
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                      <Form.Control 
                        type="number" 
                        step="0.01" 
                        name="fineness_modulus"
                        value={formData.fineness_modulus || ''}
                        readOnly
                        className="form-control form-control-sm auto-calculated" 
                        style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d', textAlign: 'center' }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ));
      case 'sieveAnalysis':
        return renderFormWithSections(2, (
          <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '1.1rem', tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '550px', padding: '10px', textAlign: 'left', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Number of Tests</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>01</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>02</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight. of sample material taken (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(A)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="crushing_weight_sample_1"
                      value={formData.crushing_weight_sample_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="crushing_weight_sample_2"
                      value={formData.crushing_weight_sample_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight. of fraction of sample passing through IS Sieve 2.36mm (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(B)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="crushing_weight_passing_1"
                      value={formData.crushing_weight_passing_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="crushing_weight_passing_2"
                      value={formData.crushing_weight_passing_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Aggregate Crushing Value = (B/A)*100</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="crushing_value_1"
                      value={formData.crushing_value_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="crushing_value_2"
                      value={formData.crushing_value_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean Aggregate Crushing Value</strong>
                  </td>
                  <td colSpan="2" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_crushing_value"
                      value={formData.mean_crushing_value || ''}
                      readOnly
                      className="form-control form-control-sm average-input-transparent" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ));
      case 'specificGravity':
        return renderFormWithSections(3, (
          <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '1.1rem', tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '550px', padding: '10px', textAlign: 'left', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Number of Tests</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>01</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>02</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>03</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight. of Sample taken (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(A)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_weight_1"
                      value={formData.impact_weight_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_weight_2"
                      value={formData.impact_weight_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_weight_3"
                      value={formData.impact_weight_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Wt. of Fraction passing through 2.36mm IS sieve (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(B)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_passing_1"
                      value={formData.impact_passing_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_passing_2"
                      value={formData.impact_passing_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_passing_3"
                      value={formData.impact_passing_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Wt. of Fraction retained on 2.36mm IS sieve (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(C)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_retained_1"
                      value={formData.impact_retained_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_retained_2"
                      value={formData.impact_retained_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_retained_3"
                      value={formData.impact_retained_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Total Wt. (B+C)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_total_weight_1"
                      value={formData.impact_total_weight_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_total_weight_2"
                      value={formData.impact_total_weight_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_total_weight_3"
                      value={formData.impact_total_weight_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Aggregate Impact Value = (B/A)*100</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_value_1"
                      value={formData.impact_value_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_value_2"
                      value={formData.impact_value_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="impact_value_3"
                      value={formData.impact_value_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean of Aggregate Impact Value</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_impact_value"
                      value={formData.mean_impact_value || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm average-input-transparent" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ));
      case 'flakinessElongation':
        return renderFormWithSections(4, (
          <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '1.1rem', tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '550px', padding: '10px', textAlign: 'left', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Number of Tests</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>01</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>02</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>03</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight of Sample taken (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(A)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_weight_sample_1"
                      value={formData.flakiness_weight_sample_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_weight_sample_2"
                      value={formData.flakiness_weight_sample_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_weight_sample_3"
                      value={formData.flakiness_weight_sample_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight of Flaky Material (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(B)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_weight_flaky_1"
                      value={formData.flakiness_weight_flaky_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_weight_flaky_2"
                      value={formData.flakiness_weight_flaky_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_weight_flaky_3"
                      value={formData.flakiness_weight_flaky_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Flakiness Index of Aggregate = (B/A)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_index_1"
                      value={formData.flakiness_index_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_index_2"
                      value={formData.flakiness_index_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="flakiness_index_3"
                      value={formData.flakiness_index_3 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean Flakiness Index</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_flakiness_index"
                      value={formData.mean_flakiness_index || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Wt. of Elongated Material (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(C)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="elongation_weight_elongated_1"
                      value={formData.elongation_weight_elongated_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="elongation_weight_elongated_2"
                      value={formData.elongation_weight_elongated_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="elongation_weight_elongated_3"
                      value={formData.elongation_weight_elongated_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Elongated Index of Aggregate = (C/A)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="elongation_index_1"
                      value={formData.elongation_index_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="elongation_index_2"
                      value={formData.elongation_index_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="elongation_index_3"
                      value={formData.elongation_index_3 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean Elongation Index</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_elongation_index"
                      value={formData.mean_elongation_index || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ));
      case 'impactValue':
        return renderFormWithSections(5, (
          <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '1.1rem', marginBottom: 0, tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '550px', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontSize: '1.1rem', fontWeight: '700' }}>Number of Tests</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #6c757d', fontSize: '1.1rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>01</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #6c757d', fontSize: '1.1rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>02</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #6c757d', fontSize: '1.1rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>03</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', paddingLeft: '12px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Volume of measure cylinder used for determination of bulk density (lit.)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="volume_measure_cylinder_1"
                      value={formData.volume_measure_cylinder_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="volume_measure_cylinder_2"
                      value={formData.volume_measure_cylinder_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="volume_measure_cylinder_3"
                      value={formData.volume_measure_cylinder_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', paddingLeft: '12px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Net weight of Rodded Sample in the measure cylinder (kg)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="net_weight_rodded_1"
                      value={formData.net_weight_rodded_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="net_weight_rodded_2"
                      value={formData.net_weight_rodded_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="net_weight_rodded_3"
                      value={formData.net_weight_rodded_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', paddingLeft: '12px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Rodded Bulk Density (kg/lit.)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="rodded_bulk_density_1"
                      value={formData.rodded_bulk_density_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="rodded_bulk_density_2"
                      value={formData.rodded_bulk_density_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="rodded_bulk_density_3"
                      value={formData.rodded_bulk_density_3 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean Rodded Bulk Density (kg/m³)</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_rodded_bulk_density"
                      value={formData.mean_rodded_bulk_density || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', paddingLeft: '12px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Net weight of Loose Sample in the measure cylinder (kg)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="net_weight_loose_1"
                      value={formData.net_weight_loose_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="net_weight_loose_2"
                      value={formData.net_weight_loose_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="net_weight_loose_3"
                      value={formData.net_weight_loose_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#1C2333' }}>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', paddingLeft: '12px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Loose Bulk Density (kg/lit.)</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="loose_bulk_density_1"
                      value={formData.loose_bulk_density_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="loose_bulk_density_2"
                      value={formData.loose_bulk_density_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="loose_bulk_density_3"
                      value={formData.loose_bulk_density_3 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean Loose Bulk Density (kg/m³)</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_loose_bulk_density"
                      value={formData.mean_loose_bulk_density || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ));
      case 'crushingValue':
        return renderFormWithSections(6, (
          <div className="table-responsive">
            <table className="table transparent-table" style={{ fontSize: '1.1rem', tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '550px', padding: '10px', textAlign: 'left', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Number of Tests</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>01</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>02</th>
                  <th style={{ width: '140px', padding: '10px', textAlign: 'center', verticalAlign: 'middle', fontSize: '1.1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>03</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight of Aggregate & Vessel and water(gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(A)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_pycnometer_aggregate_water_1"
                      value={formData.wt_pycnometer_aggregate_water_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_pycnometer_aggregate_water_2"
                      value={formData.wt_pycnometer_aggregate_water_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_pycnometer_aggregate_water_3"
                      value={formData.wt_pycnometer_aggregate_water_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight of Empty Vessel and Water(gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(B)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_pycnometer_water_1"
                      value={formData.wt_pycnometer_water_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_pycnometer_water_2"
                      value={formData.wt_pycnometer_water_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_pycnometer_water_3"
                      value={formData.wt_pycnometer_water_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight of Saturated Surface Dry Aggregate in air (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(C)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_saturated_surface_dry_1"
                      value={formData.wt_saturated_surface_dry_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_saturated_surface_dry_2"
                      value={formData.wt_saturated_surface_dry_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_saturated_surface_dry_3"
                      value={formData.wt_saturated_surface_dry_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Weight of Oven Dried Aggregate in air (gm) <span style={{ float: 'right', fontWeight: 'bold' }}>(D)</span></strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_oven_dried_1"
                      value={formData.wt_oven_dried_1 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_oven_dried_2"
                      value={formData.wt_oven_dried_2 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="wt_oven_dried_3"
                      value={formData.wt_oven_dried_3 || ''}
                      onChange={handleInputChange}
                      className="form-control form-control-sm" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Specific Gravity = [D/(D-(A-B)]</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      name="specific_gravity_1"
                      value={formData.specific_gravity_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      name="specific_gravity_2"
                      value={formData.specific_gravity_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      name="specific_gravity_3"
                      value={formData.specific_gravity_3 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean of Specific Gravity</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.001" 
                      name="mean_specific_gravity"
                      value={formData.mean_specific_gravity || ''}
                      readOnly
                      className="form-control form-control-sm average-input-transparent" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="left-align-text" style={{ fontSize: '1rem', padding: '8px', border: '1px solid #6c757d', backgroundColor: '#1C2333', color: 'white' }}>
                    <strong>Water Absorption = [(C - D)/D] * 100</strong>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="water_absorption_1"
                      value={formData.water_absorption_1 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="water_absorption_2"
                      value={formData.water_absorption_2 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#1C2333' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="water_absorption_3"
                      value={formData.water_absorption_3 || ''}
                      readOnly
                      className="form-control form-control-sm auto-calculated" 
                      style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                    />
                  </td>
                </tr>
                <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                  <td style={{ fontSize: '1rem', padding: '10px', border: '1px solid #6c757d', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>
                    <strong>Mean of Water Absorption</strong>
                  </td>
                  <td colSpan="3" style={{ padding: '8px', textAlign: 'center', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="mean_water_absorption"
                      value={formData.mean_water_absorption || ''}
                      readOnly
                      className="form-control form-control-sm average-input-transparent" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#495057', border: '1px solid #6c757d', borderRadius: '6px', color: '#ffffff', fontWeight: '700', cursor: 'not-allowed' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ));
      default:
        return null;
    }
  };

  // Show loading spinner while loading data
  if (isLoadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="warning" size="lg" className="mb-3" />
          <p style={{ color: 'var(--vitrag-gold)' }}>Loading test data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Card className="coarse-aggregate-header" style={{ backgroundColor: '#007bff !important', color: 'white !important', border: 'none', borderRadius: '15px' }}>
            <Card.Body className="coarse-aggregate-header" style={{ backgroundColor: '#007bff !important', color: 'white !important' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                   <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}><FontAwesomeIcon icon={faCubes} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />Coarse Aggregate Testing - {currentForm}</h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    {formTitles[currentForm - 1]?.description || "Test Form"}
                  </p>
                </div>
                <div>
                <Button variant="warning" className="me-2 btn-vitrag-primary" onClick={() => navigate('/other-services')}>
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Other Services
                </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
        <Form onSubmit={handleSubmit}>
          {/* Selected Tests Display */}
          {selectedTests.length > 0 && (
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm" style={{ backgroundColor: '#e3f2fd', border: '2px solid #2196f3' }}>
                  <Card.Body className="py-3">
                    <h6 className="mb-3 text-primary">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Selected Tests:
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedTests.map((testId, index) => {
                        const testInfo = allFormTitles.find(form => form.testId === testId);
                        return testInfo ? (
                          <span key={index} className="badge bg-primary me-2 mb-1 px-3 py-2" style={{ fontSize: '0.9rem' }}>
                            {testInfo.title}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Form Navigation */}
          <Row className="mb-2">
            <Col>
              <Card className="step-navigation" style={{ border: 'none' }}>
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-center">
                    {formTitles.map((form, index) => (
                      <Button
                        key={form.id}
                        variant={currentForm === (index + 1) ? "primary" : "outline-primary"}
                        size="lg"
                        className="mx-2"
                        style={{ 
                          fontSize: '1.5rem', 
                          fontWeight: 'bold', 
                          width: '80px', 
                          height: '80px', 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={() => setCurrentForm(index + 1)}
                      >
                        {index + 1}
                      </Button>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Render current form */}
          {selectedTests.length === 0 ? (
            <Row className="mb-4">
              <Col>
                <Alert variant="warning" className="text-center">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  No tests selected. Please go back and select at least one test to perform.
                </Alert>
              </Col>
            </Row>
          ) : (
            renderCurrentForm()
          )}

          {/* Error Display */}
          {submitError && (
            <Row className="mb-3">
              <Col>
                <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  {submitError}
                </Alert>
              </Col>
            </Row>
          )}

          {/* Submit Buttons */}
          <Row>
            <Col className="text-center">
              <div className="d-grid gap-2 d-md-block">
                {/* Fill Random Data button - Commented out */}
                {/* <Button 
                  type="button" 
                  variant="warning" 
                  size="lg" 
                  className="me-md-2" 
                  onClick={fillRandomData}
                >
                  <FontAwesomeIcon icon={faMagic} className="me-2" />
                  Fill Random Data
                </Button> */}
                {currentForm < formTitles.length ? (
                  <Button 
                    variant="primary" 
                    className="btn-lg me-md-2 btn-vitrag-primary" 
                    onClick={() => setCurrentForm(currentForm + 1)}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save and Next
                  </Button>
                ) : (
                  <Button 
                    type="submit"
                    variant="success" 
                    className="btn-lg btn-vitrag-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        Save Test Data
                      </>
                    )}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Form>
    </div>
  );
};

export default CoarseAggregateForm;
